import { enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import {
  IonicRouteStrategy,
  LoadingController,
  provideIonicAngular
} from '@ionic/angular/standalone';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { defineCustomElements } from '@ionic/pwa-elements/loader'; // Keep this - needed for Capacitor plugins
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import * as ionIcons from 'ionicons/icons';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { successResponseHandlerInterceptor } from '@core/interceptors/success-response-handler.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { authErrorInterceptor } from '@core/interceptors/auth-error.interceptor';
import { mockInterceptor } from '@core/mocks/mock.interceptor';
import { isMockDevToolsEnabled } from '@core/mocks/mock-devtools.config';
import { LocalDatePipe } from '@shared/pipes/local-date.pipe';
import { InsuranceTypePipe } from '@features/insurances/pipes/insurance-type.pipe';
import { NavigationService } from '@core/services/navigation.service';

if (environment.production) {
  enableProdMode();
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

addIcons({ ...ionIcons });

// Initialize PWA Elements for web platform
// This is REQUIRED for Capacitor plugins (Camera, etc.) to work in web browsers
defineCustomElements(window);

async function initializeApp() {
  const appRef = await bootstrapApplication(AppComponent, {
    providers: [
      provideAnimationsAsync(),
      provideIonicAngular(),
      provideHttpClient(
        withInterceptors([
          ...(isMockDevToolsEnabled() ? [mockInterceptor] : []),
          authInterceptor,
          successResponseHandlerInterceptor,
          authErrorInterceptor,
          httpErrorInterceptor
        ])
      ),
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      LoadingController,
      NavigationService,
      importProvidersFrom([
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: createTranslateLoader,
            deps: [HttpClient]
          }
        })
      ]),
      provideRouter(routes),
      LocalDatePipe,
      InsuranceTypePipe
    ]
  });

  // Force instantiate the NavigationService after bootstrap
  const injector = appRef.injector;
  injector.get(NavigationService);
  injector.get(LoadingController);

  return appRef;
}

initializeApp().catch((err) => console.error('Error bootstrapping app:', err));
